import { ReactElement, useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  ColumnDef,
} from '@tanstack/react-table';
import TableSkeleton from './TableSkeleton';

interface TableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  onRowClick?: (e: React.MouseEvent, row: T) => void;
  onRowDoubleClick?: (e: React.MouseEvent, row: T) => void;
  isLoading?: boolean;
  rowClassName?: (row: T) => string;
}

// Helper function to determine rounded corner classes for table cells
function getCornerClass<T>(arr: T[], idx: number, isTop: boolean): string {
  const isFirstCell = idx === 0;
  const isLastCell = idx === arr.length - 1;
  if (isTop) {
    if (isFirstCell) return ' cr-rounded-tl-lg';
    if (isLastCell) return ' cr-rounded-tr-lg';
  } else {
    if (isFirstCell) return ' cr-rounded-bl-lg';
    if (isLastCell) return ' cr-rounded-br-lg';
  }
  return '';
}

export default function Table<T>(
  { data, columns, onRowClick, onRowDoubleClick, isLoading, rowClassName }: TableProps<T>,
): ReactElement {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const handleRowClick = (e: React.MouseEvent, row: T) => {
    // Don't trigger row click if clicking on buttons or menus
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }

    onRowClick?.(e, row);
  };

  const handleRowDoubleClick = (e: React.MouseEvent, row: T) => {
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    onRowDoubleClick?.(e, row);
  };

  if (isLoading) {
    return <TableSkeleton columns={columns.length} />;
  }

  return (
    <div className="cr-mb-8 cr-overflow-x-auto">
      <table className="cr-min-w-full cr-border-separate cr-border-spacing-0 cr-rounded-b-lg cr-rounded-t-lg cr-border-2 cr-border-solid cr-border-cirrusBlue-100 cr-bg-cirrusBlue-25 cr-font-main cr-text-[12px] md:cr-text-[14px]">
        <thead>
          {table.getHeaderGroups().map((headerGroup, headerGroupIdx) => (
            <tr
              key={headerGroup.id}
              className={
                'cr-rounded-t-lg cr-border-2 cr-border-solid cr-border-cirrusBlue-100 cr-p-3 cr-text-[14px] cr-text-cirrusBlue-400 md:cr-text-[16px]' +
                (headerGroupIdx === 0 ? ' cr-rounded-b-0 cr-bg-cirrusBlue-50 ' : '') +
                (headerGroupIdx === table.getHeaderGroups().length - 1 ? ' cr-font-secondary cr-uppercase' : '')
              }
              style={{ fontFamily: '\'Poppins\', sans-serif' }}
            >
              {headerGroup.headers.map((header, headerIdx) => {
                let thClass =
                  'cr-px-3 cr-py-2 cr-text-left cr-text-cirrusBlue-400 cr-border-0 cr-border-b-2 cr-border-cirrusBlue-100 cr-border-solid';
                thClass += getCornerClass(headerGroup.headers, headerIdx, true);

                // Add sorting styles for sortable columns
                if (header.column.getCanSort()) {
                  thClass += ' cr-cursor-pointer cr-select-none hover:cr-bg-cirrusBlue-100';
                }

                return (
                  <th
                    key={header.id}
                    className={thClass}
                    onClick={header.column.getToggleSortingHandler()}
                    colSpan={header.colSpan}
                    style={{ fontFamily: '\'Poppins\', sans-serif', fontWeight: '600' }}
                  >
                    <div className="cr-flex cr-items-center cr-justify-between">
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <span className="cr-ml-1">
                          {{
                            asc: '↑',
                            desc: '↓',
                          }[header.column.getIsSorted() as string] ?? '↕'}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, idx, arr) => (
            <tr
              key={row.id}
              className={`cr-cursor-pointer cr-bg-cirrusBlue-25 cr-font-main cr-text-[12px] cr-transition-colors cr-duration-200 hover:cr-bg-cirrusBlue-50 md:cr-text-[14px] ${idx === 0 ? 'cr-border-t-0' : ''} ${rowClassName?.(row.original) ?? ''}`}
              onClick={(e) => handleRowClick(e, row.original)}
              onDoubleClick={(e) => handleRowDoubleClick(e, row.original)}
            >
              {row.getVisibleCells().map((cell, cellIdx) => {
                let tdClass = 'cr-px-3 cr-py-2 cr-text-cirrusBlue-400';
                if (idx !== arr.length - 1) tdClass += ' cr-border-b-2 cr-border-cirrusBlue-100 cr-border-solid';
                else tdClass += getCornerClass(row.getVisibleCells(), cellIdx, false);
                return (
                  <td key={cell.id} className={tdClass}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
